import React, { useContext, useMemo } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { modeUIContext, selectCurrentUsageMode, UsageMode } from '@moderepo/mode-ui-state';
import { useGetURLParamEntityIdAsBoolean, useGetURLParamEntityIdAsNumber, useGetURLParamEntityIdAsString } from '../../utils/utils';
import { EntitiesListingContainer, EntitiesListingContainerProps, EntityBasicViewDesign, ListCardViewTypeOptions } from '.';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { EntitiesListingViewType } from '../../components';
import { useSelectProject } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';


const useStyle = makeStyles(() => {
    return {
        root: {
        },

        entitiesListingContainer: {
            width: '100%',
        },
    };
}, { name: 'EntitiesBrowserPage', index: 1 });



/**
 * This is a page that display all entities. This will display all PROJECT entities or all HOME entities depending on the route.
 * If the user is in a route that has homeId in the URL, this page will display all HOME entities. If the user is NOT in a route
 * that has homeId then this page will display all PROJECT entities
 */
export const EntitiesBrowserPage: React.FC = () => {

    const { state:uiState } = useContext(modeUIContext);
    const usageMode = selectCurrentUsageMode(uiState);

    // Get the params from the URL
    const projectId = useGetURLParamEntityIdAsNumber('projectId');
    const homeId = useGetURLParamEntityIdAsNumber('homeId');
    const parentId = useGetURLParamEntityIdAsString('parentId');
    const entityClasses = useGetURLParamEntityIdAsString('entityClasses');
    const viewType = useGetURLParamEntityIdAsString('viewType') as EntitiesListingViewType;

    // Options for viewType GRID and CARD
    const showAttributes = useGetURLParamEntityIdAsBoolean('showAttributes');
    const showImage = useGetURLParamEntityIdAsBoolean('showImage');
    const showIcon = useGetURLParamEntityIdAsBoolean('showIcon');
    const showEntityClass = useGetURLParamEntityIdAsBoolean('showEntityClass');
    const viewDesign = useGetURLParamEntityIdAsString('viewDesign') as EntityBasicViewDesign;


    // Get the GRID/CARD view options from the params and create a ListCardViewTypeOptions object to be passed to the EntitiesListingContainer
    const listCardViewTypeOptions = useMemo((): ListCardViewTypeOptions | undefined => {

        // Put all the options we got from the URL into an object, some or all of them might be undefined
        const allOptions: ListCardViewTypeOptions = {
            showAttributes, showImage, showIcon, showEntityClass, viewDesign,
        };

        // Filter out the undefined options. We don't want to include the options with undefined values because it will override the default options
        const filteredOptions = Object.entries(allOptions).reduce((
            result: ListCardViewTypeOptions,
            [optionName, optionValue],
        ) => {
            if (optionValue !== undefined) {
                return {
                    ...result,
                    [optionName]: optionValue,
                };
            }
            return result;
        }, {});


        // Now check if there is at least 1 option that is NOT undefined
        const hasAtLeastOneOptions = Object.values(filteredOptions).some((optionValue) => {
            return optionValue !== undefined;
        });

        // If there is at least one then create and return a ListCardViewTypeOptions object. If not then just return undefined.
        if (hasAtLeastOneOptions) {
            return filteredOptions;
        }

        return undefined;
    }, [showAttributes, showEntityClass, showIcon, showImage, viewDesign]);


    const classes = useStyle();

    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);


    /**
     * If entityClass is specified in the URL, create `presetFilters` and pass it to the Entities listing component.
     * TODO - Add support for other filters e.g. search
     */
    const presetFilters = useMemo((): EntitiesListingContainerProps['presetFilters'] | undefined => {
        if (entityClasses) {
            const classes = entityClasses.split(',');
            if (classes && classes.length > 0) {
                return {
                    entityClasses: classes.map((entityClass) => {
                        // trim all left/right spaces
                        return entityClass.trim();
                    }).filter((entityClass) => {
                        // filter out empty strings
                        return entityClass.length > 0;
                    }),
                };
            }
        }
        return undefined;
    }, [entityClasses]);



    return (
        <div className={clsx('page-main-content', classes.root)}>
            {/**
             * Don't show the page title if the user is in VIEW DATA mode
             */}
            {usageMode !== UsageMode.VIEW_DATA && (
                <div className="title-bar">
                    <div className="title-bar-item title-text">
                        <div className="page-title">
                            {trans('page.title.entities_browser')}
                        </div>
                        <div className="page-subtitle">
                            {trans('page.subtitle.entities_browser')}
                        </div>
                    </div>
                </div>
            )}

            {projectId && (
                /*
                    NOTE: when the filter params changed, we need to give the component a new key to force react to rerender this component.
                    React doesn't rerender this component when the user changed e.g. from https://cs.tinkermode.com/entities to
                    https://cs.tinkermode.com/entities?entityClasses=abc,def. Because the URL pathname is the same and only the
                    query params changed, react will not rerender the component.
                */
                <EntitiesListingContainer
                    key={JSON.stringify(presetFilters)}
                    className={clsx(classes.entitiesListingContainer)}
                    projectId={projectId}
                    parentId={parentId}
                    homeId={homeId}
                    viewType={Object.values(EntitiesListingViewType).includes(viewType) ? viewType : undefined}
                    presetFilters={presetFilters}
                    canCreateEntities
                    stickyControlBar
                    listCardViewTypeOptions={listCardViewTypeOptions}
                />
            )}

            {!projectId && (
                <CircularProgress />
            )}
        </div>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntitiesBrowserPage', EntitiesBrowserPage);
